import {
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useStyles } from './styles'
import type { IReservation } from './types'
import { ReservationListSkeleton } from './ReservationListSkeleton'
import { convertArrayToDateTime } from '@/utils/date'
import { Grid, Text } from '@conexasaude/conexa-hero'
import { useReservationConsultationContext } from '../../contexts/consultationReservationContext'
import { pluralizeWord } from '@/utils/string'
import { useReservationsConsultations } from '../../queries'
import { PreScheduleCard } from '../PreScheduleCard'
import { autoCapitalizeName } from '@/utils/autoCapitalizeName'
import { UpcomingConsultationMapper } from '../../mappers/UpcomingConsultationMapper'
import { UpcomingConsultation } from '../../types'
import { ConfirmConsultationModal } from '../modals/ConfirmConsultationModal'
import { RenderIf } from '@/components/RenderIf'
import { CancelPreScheduleConfirmationModal } from '../modals/CancelPreScheduleConfirmationModal'
import { autoCapitalize } from '@conexasaude/front-utils/formatters'
import { FeedbackRefuseReservationModal } from '../modals/FeedbackRefuseReservationModal'

const ReservationList: FunctionComponent = () => {
  const styles = useStyles()

  const [selectedReservation, setSelectedReservation] =
    useState<IReservation | null>(null)

  const { setReservationConsultationData } = useReservationConsultationContext()
  const { data: reservationsData, isLoading } = useReservationsConsultations()

  useEffect(() => {
    if (!isLoading) {
      setReservationConsultationData(reservationsData)
    }
  }, [isLoading, reservationsData, setReservationConsultationData])

  const mountUpcomingConsultationDataToShowModalConfirmation = useMemo(() => {
    if (!selectedReservation) return

    const data = UpcomingConsultationMapper.toDomain(
      selectedReservation as unknown as UpcomingConsultation,
    )

    return data
  }, [selectedReservation])

  return (
    <Fragment>
      <Text variant="headingLarge" css={styles.title}>
        Confirme suas Reservas
      </Text>
      <Text renderAs="p">
        Você tem {reservationsData?.length || 0}{' '}
        {pluralizeWord('horário', 'horários', reservationsData?.length)}{' '}
        {pluralizeWord('reservado', 'reservados', reservationsData?.length)}
        {''} para confirmar
      </Text>

      <Grid gutter="md" mt="lg">
        {reservationsData?.map((reservations: IReservation) => {
          return (
            <Grid.Col
              span={{ sm: 6, lg: 4, xs: 12 }}
              key={reservations?.idAtendimento}
              onClick={() => {
                setReservationConsultationData({
                  ...reservations,
                })
              }}
            >
              <PreScheduleCard
                professional={{
                  id: reservations?.idProfissional,
                  name: autoCapitalizeName(reservations?.nomeProfissional),
                  specialty: reservations?.especialidade,
                  photo: reservations?.urlFoto,
                }}
                preSchedule={{
                  id: reservations?.idAtendimento,
                  date: reservations?.dataAtendimentoFmt,
                  isPreScheduleConfirmed: reservations?.reservaConfirmada,
                  confirmationLimitDate: convertArrayToDateTime(
                    reservations?.confirmacaoAtendimento.prazoConfirmacao,
                  ),
                  createdAt: convertArrayToDateTime(
                    reservations?.dataHoraCriacaoAtendimento,
                  ),
                }}
                callbacks={{
                  onCardAnyActions: () => {
                    setSelectedReservation(reservations)
                  },
                }}
              />
            </Grid.Col>
          )
        })}
      </Grid>

      <RenderIf condition={!!selectedReservation}>
        <CancelPreScheduleConfirmationModal
          data={{
            preScheduleDate: selectedReservation?.dataAtendimentoFmt,
            preScheduleId: selectedReservation?.idAtendimento,
            professionalName: autoCapitalize(
              selectedReservation?.nomeProfissional || '',
            ),
          }}
        />

        <FeedbackRefuseReservationModal
          specialty={selectedReservation?.tipoProfissional?.id}
        />

        <ConfirmConsultationModal
          {...mountUpcomingConsultationDataToShowModalConfirmation}
        />
      </RenderIf>
    </Fragment>
  )
}

export { ReservationList, ReservationListSkeleton }
